/**
 * Memoized scenario card component.
 * Extracted from ConsumerHomepage so React can skip re-rendering
 * cards whose props haven't changed.
 */
import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heart, Baby, Ambulance, Activity, ExternalLink, Star, MapPin, HandHeart, ChevronDown, Info, Layers } from "lucide-react";
import { ScenarioHowItWorksModal } from "@/components/ScenarioHowItWorksModal";
import { ScenarioFamilyModal, type RawScenario as FamilyModalScenario } from "@/components/ScenarioFamilyModal";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { loadScenarioById, loadScenarioIntlPricing } from "@/lib/scenarioDetailStore";

/**
 * Per-country international pricing precomputed by scripts/generate_static_cache.py
 * and embedded in scenarios/{uuid}.json (web) + scenarios.json (Capacitor app).
 *
 * Web flow: card lazy-fetches the per-scenario JSON via loadScenarioById on
 *           first flag-click. ONE CDN fetch, cached in memory + IndexedDB.
 * App flow: scenarios.json is bundled locally, so internationalPricing arrives
 *           with the scenario data and the card uses it directly without any
 *           additional fetch.
 */
export interface InternationalPricingEntry {
  name: string;
  flag: string;
  total: number;
  phases: Record<string, number>;
  mapped: number;
  totalCodes: number;
  /** US national cost of the SAME mapped CPT subset (international_scenario_costs
   *  MV keeps intl_total_cost and us_national_total_cost over identical codes).
   *  Percentage/multiplier math MUST use this when present — comparing a
   *  partial-subset intl total against the FULL scenario cost produced absurd
   *  "442× cheaper" claims. */
  usTotal?: number;
}
export type InternationalPricing = Record<string, InternationalPricingEntry>;

// Phase colors — first 3 slots track the active brand's primary trio
// (red/blue/gold for AHP, matrix-green/blood-red/cyan for Luigi's, etc.).
// Built per-instance from the theme prop in the component body so swapping
// brands doesn't leave AHP-patriotic stripes on Luigi's-skinned scenario
// breakdowns. Slots 4-6 are neutral colors that read well on both brands.
const PHASE_NEUTRAL_TAIL = ["#2b5e49", "#6B4C8A", "#D4763B"];
const ICON_MAP: Record<string, any> = { Heart, Baby, Ambulance, Activity };

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";


/** Design tokens for theming — passed as a single prop to avoid many individual props */
export interface CardTheme {
  cardBg: string;
  border: string;
  shadow: string;
  /** Optional inset top-edge highlight for dark-mode card lift. In light mode
   *  this can be omitted (or set to a very subtle white) and the card still
   *  reads correctly because the ambient shadow does the work. In dark mode
   *  the ambient shadow disappears against the page bg, so a 1px inset top
   *  highlight is what actually communicates "raised card." Leave undefined
   *  to preserve legacy single-shadow rendering. */
  shadowTopHighlight?: string;
  red: string;
  gold: string;
  green: string;
  blue: string;
  text: string;
  textMuted: string;
}

export interface ScenarioCardData {
  id: string;
  name: string;
  description: string;
  icon_name: string;
  color: string;
  duration: string;
  total_procedures: number;
  phases: string[];
  severity_level: number | null;
  calculated_avg_cost: number;
  /** Median-anchored scenario total. Falls back to avg_cost when scenario_costs_cache
   *  hasn't been refreshed with median data yet (older entries). Drives the avg/median
   *  toggle on the card. */
  calculated_median_cost?: number;
  phaseBreakdown: Record<string, number>;
  /** Per-phase median totals — same shape as phaseBreakdown but using median-anchored
   *  per-code prices. Optional; null when scenario_costs_cache lacks median data. */
  phaseBreakdownMedian?: Record<string, number> | null;
  phaseProcedures: Record<string, any[]>;
  tags: string[];
  created_by: string | null;
  is_verified: boolean;
  /** Pre-computed per-country pricing. Present when the parent loaded
   *  the full scenario bundle (Capacitor app or detail page); absent on
   *  the web home page index, in which case the card lazy-loads it. */
  internationalPricing?: InternationalPricing;
}

interface ScenarioCardProps {
  scenario: ScenarioCardData;
  compact?: boolean;
  isStarred: boolean;
  theme: CardTheme;
  onToggleBookmark: (id: string) => void;
  /** @deprecated Share button moved off the card; prop is unused but kept for callers. */
  onShare?: (scenario: ScenarioCardData) => void;
  /** @deprecated Template button moved off the card; prop is unused but kept for callers. */
  onUseAsTemplate?: (scenario: ScenarioCardData) => void;
  /** When set, overrides the displayed cost with a hospital-specific blended price.
   *  Shows markup badge and coverage info below the price. */
  hospitalOverride?: {
    blendedPrice: number;
    blendedMarkup: number;
    matchedCodes: number;
    totalCodes: number;
    nationalCost: number;
    hospitalId?: string;
    /** Force price color regardless of markup. 'expensive' = red, 'deal' = green */
    priceContext?: 'expensive' | 'deal';
    /** USA rank info for this hospital + scenario (rendered as a corner badge in hospital mode). */
    usaRank?: {
      expensive: number | null;
      cheap: number | null;
      total: number | null;
      percentile: number | null;
    };
  };
  /** Family siblings (for bottom severity bar). Pass array of sibling scenarios sorted by severity_level.
   *  When provided, shows "X scenarios" + severity pills (0-4) at the bottom of the card.
   *  `estimatedCost` is optional but enables the family-explorer modal opened from the card to show
   *  cost-range chips per severity level without a second fetch. */
  familySiblings?: Array<{
    id: string;
    severity_level: number | null;
    name: string;
    estimatedCost?: number | null;
    /** Per-variant procedure count, threaded through to ScenarioFamilyModal so
     *  the variant rows render real proc counts instead of "0 procedures". */
    totalProcedures?: number | null;
  }>;
  /** Scenario-page embed: replaces the footer CTAs with a table of contents
   *  over the page's own sections. Chips call onSectionNav(key). */
  sectionNav?: Array<{ key: string; label: string; icon?: React.ElementType }>;
  onSectionNav?: (key: string) => void;
}

// Country selector persistence — one global default across all cards
const COUNTRY_STORAGE_KEY = "ahp-card-country";
function getInitialCountry(): string {
  if (typeof window === "undefined") return "US";
  return window.localStorage.getItem(COUNTRY_STORAGE_KEY) || "US";
}

export const ScenarioCard = React.memo(function ScenarioCard({
  scenario,
  compact = false,
  isStarred,
  theme: T,
  onToggleBookmark,
  hospitalOverride,
  familySiblings,
  sectionNav,
  onSectionNav,
}: ScenarioCardProps) {
  const { t } = useTranslation(['homepage', 'common']);
  const IconComponent = ICON_MAP[scenario.icon_name] || Activity;
  // Per-phase bar colors. First 3 slots ride the theme so AHP gets red→navy→gold
  // and Luigi's gets matrix-green→blood-red→cyan, etc. Remaining slots are
  // neutral so deep multi-phase scenarios don't tile garishly.
  const phaseColors = useMemo(
    () => [T.red, T.blue, T.gold, ...PHASE_NEUTRAL_TAIL],
    [T.red, T.blue, T.gold]
  );
  const breakdown = (typeof scenario.phaseBreakdown === 'object' && scenario.phaseBreakdown) ? scenario.phaseBreakdown : {};
  const phases = (Array.isArray(scenario.phases) ? scenario.phases : Object.keys(breakdown))
    .sort((a: any, b: any) => (breakdown[b] || 0) - (breakdown[a] || 0));
  const maxPhaseCost = Math.max(...Object.values(breakdown), 1);
  const slug = scenario.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const authorName = scenario.created_by || t('homepage:homeBeta.scenarioCard.communityAuthor');

  // Country selector. International pricing comes from one of two paths:
  // 1) Pre-bundled in the scenario prop (Capacitor app, scenario detail page)
  // 2) Lazy-fetched from R2 CDN on first flag-click via loadScenarioById (web)
  // Either way, ONE fetch (or zero), and the result is cached in memory.
  const [selectedCountry, setSelectedCountry] = useState<string>(getInitialCountry);
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [fetchedIntl, setFetchedIntl] = useState<InternationalPricing | null>(null);
  const [intlLoading, setIntlLoading] = useState(false);

  // Effective international pricing — prop wins, then fetched, then null.
  const intlPricing: InternationalPricing | null =
    scenario.internationalPricing && Object.keys(scenario.internationalPricing).length > 0
      ? scenario.internationalPricing
      : fetchedIntl;

  // Lazy fetch international pricing the FIRST time the picker is opened.
  // Loads international_stats.json once (cached for session), then filters
  // for this scenario. No per-scenario network request needed.
  useEffect(() => {
    if (!countryPickerOpen) return;
    if (intlPricing) return;
    if (fetchedIntl !== null) return; // already attempted (may be empty {})
    let cancelled = false;
    setIntlLoading(true);
    loadScenarioIntlPricing(scenario.id).then((pricing) => {
      if (cancelled) return;
      setFetchedIntl((pricing as InternationalPricing) || {});
      setIntlLoading(false);
    });
    return () => { cancelled = true; };
  }, [countryPickerOpen, intlPricing, fetchedIntl, scenario.id]);

  // Build the dropdown list — US is always first, then any countries with data.
  const availableCountries = useMemo(() => {
    const list: Array<{ code: string; name: string; flag: string }> = [
      { code: "US", name: "United States", flag: "🇺🇸" },
    ];
    if (intlPricing) {
      const codes = Object.keys(intlPricing).sort((a, b) => intlPricing[a].name.localeCompare(intlPricing[b].name));
      for (const code of codes) {
        list.push({ code, name: intlPricing[code].name, flag: intlPricing[code].flag });
      }
    }
    return list;
  }, [intlPricing]);

  // Resolve current country meta + cost + breakdown.
  const currentCountry = availableCountries.find(c => c.code === selectedCountry) || availableCountries[0];
  const currentEntry: InternationalPricingEntry | null =
    selectedCountry !== "US" && intlPricing ? intlPricing[selectedCountry] || null : null;

  // "How it works" modal — opens a Dignity-narrated breakdown of code list,
  // cornerstones, and methodology. Lazy-loaded data inside the modal so it
  // doesn't pay the SQL cost on every card mount.
  const [howItWorksOpen, setHowItWorksOpen] = React.useState(false);

  // Family explorer modal — opened from the bottom severity bar. Pre-scrolls
  // to a specific level when one is clicked, falls back to base (0) when the
  // "explore family" affordance is used.
  const [familyModalOpen, setFamilyModalOpen] = React.useState(false);
  const [familyModalLevel, setFamilyModalLevel] = React.useState<number | null>(null);
  // Build the slim raw-scenario list the modal expects from familySiblings + self.
  // The modal filters allRawScenarios by scenario_family_id, so we mint a stable
  // synthetic family_id (derived from the current scenario id) and tag every
  // entry with it. Cheap to do here — only invoked when the modal renders, and
  // there are at most ~20 entries per family.
  const familySyntheticId = `card-family-${scenario.id}`;
  const familyAllScenarios: FamilyModalScenario[] = useMemo(() => {
    if (!familySiblings || familySiblings.length === 0) return [];
    return [
      {
        id: scenario.id,
        name: scenario.name,
        description: scenario.description,
        icon_name: scenario.icon_name,
        color: scenario.color,
        severityLevel: scenario.severity_level ?? 0,
        scenario_family_id: familySyntheticId,
        estimatedCost: scenario.calculated_avg_cost,
        // Otherwise the base row in the family modal shows "0 procedures"
        // while siblings show real counts (or worse, all zeros if siblings
        // also weren't wired). Always thread proc count through.
        totalProcedures: scenario.total_procedures ?? 0,
        phaseBreakdown: scenario.phaseBreakdown,
      } as FamilyModalScenario,
      ...familySiblings.map((s) => ({
        id: s.id,
        name: s.name,
        severityLevel: s.severity_level ?? 0,
        scenario_family_id: familySyntheticId,
        estimatedCost: s.estimatedCost ?? 0,
        totalProcedures: s.totalProcedures ?? 0,
      } as FamilyModalScenario)),
    ];
  }, [
    familySiblings, familySyntheticId,
    scenario.id, scenario.name, scenario.description, scenario.icon_name,
    scenario.color, scenario.severity_level, scenario.calculated_avg_cost,
    scenario.phaseBreakdown,
  ]);

  // Avg/median toggle — applies only to the US (national-aggregate) view.
  // International prices are pre-computed per-country totals from currentEntry,
  // hospital-override prices are blended per-hospital; neither has a meaningful
  // median/avg distinction at the per-card level.
  // Persisted globally so flipping the toggle on one card persists to all.
  const [costStat, setCostStat] = React.useState<'avg' | 'median'>(() => {
    if (typeof window === 'undefined') return 'avg';
    return (window.localStorage.getItem('ahp-card-cost-stat') as 'avg' | 'median') || 'avg';
  });
  const handleCostStatChange = (next: 'avg' | 'median') => {
    setCostStat(next);
    try { window.localStorage.setItem('ahp-card-cost-stat', next); } catch {}
  };
  const hasMedian = scenario.calculated_median_cost != null
    && scenario.calculated_median_cost !== scenario.calculated_avg_cost;
  const showStatToggle = !hospitalOverride && !currentEntry && hasMedian;
  const usCostForStat = costStat === 'median' && hasMedian
    ? (scenario.calculated_median_cost as number)
    : scenario.calculated_avg_cost;

  const currentCost = hospitalOverride
    ? hospitalOverride.blendedPrice
    : (currentEntry ? currentEntry.total : usCostForStat);

  // Near-zero price framing (DISPLAY only — hospital-filed prices are never
  // hidden): a blended price under $100 or under 1% of the national cost is
  // almost certainly a chargemaster filing artifact ($1 placeholders, per-unit
  // prices filed as totals). Don't celebrate it as a deal — label it as the
  // hospital's own filed number and let the user verify.
  const suspiciouslyCheap = !!hospitalOverride
    && hospitalOverride.blendedPrice < Math.max(100, 0.01 * hospitalOverride.nationalCost);

  // Per-country phase breakdown — comes directly from precomputed JSON.
  // No estimation; phases that have no CPT mapping for that country aren't shown.
  // When hospitalOverride is present, scale national phase proportions by hospital blended total.
  const baseBreakdown = currentEntry?.phases ?? breakdown;
  const intlBreakdown = useMemo(() => {
    if (!hospitalOverride || Object.keys(baseBreakdown).length === 0) return baseBreakdown;
    const nationalTotal = Object.values(baseBreakdown).reduce((s, v) => s + v, 0);
    if (nationalTotal <= 0) return baseBreakdown;
    const scale = hospitalOverride.blendedPrice / nationalTotal;
    const scaled: Record<string, number> = {};
    for (const [phase, cost] of Object.entries(baseBreakdown)) {
      scaled[phase] = Math.round(cost * scale);
    }
    return scaled;
  }, [baseBreakdown, hospitalOverride]);
  const intlMaxPhaseCost = Math.max(...Object.values(intlBreakdown), 1);

  const handleCountryChange = (code: string) => {
    setSelectedCountry(code);
    setCountryPickerOpen(false);
    try { window.localStorage.setItem(COUNTRY_STORAGE_KEY, code); } catch {}
  };

  return (
    <div
      className="flex flex-col h-full relative"
      style={{
        backgroundColor: T.cardBg,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: T.shadowTopHighlight
          ? `0 1px 2px ${T.shadow}, 0 6px 16px ${T.shadow}, inset 0 1px 0 ${T.shadowTopHighlight}`
          : `0 2px 16px ${T.shadow}`,
        transition: "box-shadow 0.2s, border-color 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = T.red;
        e.currentTarget.style.boxShadow = T.shadowTopHighlight
          ? `0 2px 4px ${T.shadow}, 0 8px 22px rgba(178,34,52,0.18), inset 0 1px 0 ${T.shadowTopHighlight}`
          : `0 4px 24px rgba(178,34,52,0.10)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = T.border;
        e.currentTarget.style.boxShadow = T.shadowTopHighlight
          ? `0 1px 2px ${T.shadow}, 0 6px 16px ${T.shadow}, inset 0 1px 0 ${T.shadowTopHighlight}`
          : `0 2px 16px ${T.shadow}`;
      }}
    >
      {/* Star / Bookmark button — top right (hidden in hospital expansion mode) */}
      {!hospitalOverride && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleBookmark(scenario.id); }}
          className="absolute top-3 right-3 z-10 flex items-center justify-center w-8 h-8 rounded-full transition-all"
          style={{
            // Quiet ghost icon until followed — the bordered pill floating in
            // the corner whitespace read as a broken orphaned button.
            backgroundColor: isStarred ? "rgba(197,165,90,0.14)" : "transparent",
            border: `1px solid ${isStarred ? T.gold : "transparent"}`,
            cursor: "pointer",
          }}
          aria-label={isStarred ? "Unfollow scenario" : "Follow scenario"}
          aria-pressed={isStarred}
          title={isStarred
            ? t('homepage:homeBeta.scenarioCard.bookmarkRemove', { defaultValue: 'Following — tap to remove from your shortlist' })
            : t('homepage:homeBeta.scenarioCard.bookmarkAdd', { defaultValue: 'Follow this scenario — save it to your shortlist' })}
        >
          <Star
            className="w-3.5 h-3.5"
            style={{ color: isStarred ? T.gold : T.textMuted }}
            fill={isStarred ? T.gold : "none"}
          />
        </button>
      )}

      <div className={hospitalOverride ? "p-3 flex-1 flex flex-col" : compact ? "p-4 flex-1 flex flex-col" : "p-5 flex-1 flex flex-col"}>
        {/* Top row: Icon + Title + Complexity */}
        <div className={hospitalOverride ? "flex items-start gap-2 mb-2" : "flex items-start gap-3 mb-3"}>
          <div
            className={hospitalOverride ? "p-1.5 rounded-lg flex-shrink-0" : "p-2.5 rounded-lg flex-shrink-0"}
            style={{ backgroundColor: `${scenario.color}15` }}
          >
            <IconComponent className={hospitalOverride ? "w-4 h-4" : "w-6 h-6"} style={{ color: scenario.color }} />
          </div>
          <div className={hospitalOverride ? "flex-1 min-w-0" : "flex-1 min-w-0 pr-10"}>
            <h3
              className="leading-tight"
              style={{
                fontFamily: serif,
                fontSize: hospitalOverride ? 15 : compact ? 17 : "clamp(18px, 2.5vw, 22px)",
                fontWeight: 400,
                color: T.text,
              }}
            >
              {scenario.name}
            </h3>
            {/* USA rank badge (hospital mode). Lives in normal flow under the
                title — it used to float absolute in the card corner, where
                long titles ran underneath it. */}
            {hospitalOverride?.usaRank && hospitalOverride.usaRank.total && hospitalOverride.usaRank.total >= 10 && !suspiciouslyCheap && (() => {
              const r = hospitalOverride.usaRank!;
              const total = r.total!;
              const isExpensiveTopDecile = r.expensive != null && r.expensive <= total * 0.10;
              const isCheapTopDecile = r.cheap != null && r.cheap <= total * 0.10;
              if (isExpensiveTopDecile && r.expensive != null) {
                return (
                  <span
                    className="inline-block mt-1 text-[9px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-[2px] border whitespace-nowrap"
                    style={{ background: 'rgba(178,34,52,0.12)', borderColor: 'rgba(178,34,52,0.55)', color: '#8B1828' }}
                    title={`#${r.expensive.toLocaleString()} most expensive in USA out of ${total.toLocaleString()} hospitals`}
                  >
                    🇺🇸 #{r.expensive.toLocaleString()} most expensive
                  </span>
                );
              }
              if (isCheapTopDecile && r.cheap != null) {
                return (
                  <span
                    className="inline-block mt-1 text-[9px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-[2px] border whitespace-nowrap"
                    style={{ background: 'rgba(15,138,95,0.12)', borderColor: 'rgba(15,138,95,0.55)', color: '#0F6B49' }}
                    title={`#${r.cheap.toLocaleString()} cheapest in USA out of ${total.toLocaleString()} hospitals`}
                  >
                    🇺🇸 #{r.cheap.toLocaleString()} cheapest
                  </span>
                );
              }
              if (r.percentile != null) {
                return (
                  <span
                    className="inline-block mt-1 text-[9px] font-semibold uppercase tracking-wider rounded-sm px-1.5 py-[2px] border whitespace-nowrap"
                    style={{ background: 'rgba(197,165,90,0.12)', borderColor: 'rgba(197,165,90,0.45)', color: '#7A5F1F' }}
                    title={`${r.percentile.toFixed(0)}th percentile of ${total.toLocaleString()} USA hospitals (100 = most expensive)`}
                  >
                    USA p{Math.round(r.percentile)}
                  </span>
                );
              }
              return null;
            })()}
            {!hospitalOverride && (
              <p
                className={compact ? "mt-1 line-clamp-1" : "mt-1 line-clamp-2"}
                style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.45 }}
              >
                {scenario.description}
              </p>
            )}
          </div>
          {!compact && scenario.severity_level != null && scenario.severity_level > 0 && (
            <span
              className="flex-shrink-0 text-xs font-medium px-2 py-1 rounded"
              style={{
                backgroundColor:
                  scenario.severity_level >= 7
                    ? "rgba(178,34,52,0.08)"
                    : scenario.severity_level >= 4
                    ? "rgba(197,165,90,0.12)"
                    : "rgba(43,94,73,0.08)",
                color: scenario.severity_level >= 7 ? T.red : scenario.severity_level >= 4 ? T.gold : T.green,
              }}
            >
              {scenario.severity_level >= 7
                ? t('homepage:homeBeta.scenarioCard.severityHigh')
                : scenario.severity_level >= 4
                ? t('homepage:homeBeta.scenarioCard.severityMed')
                : t('homepage:homeBeta.scenarioCard.severityLow')}
            </span>
          )}
        </div>

        {/* Cost + meta row */}
        <div className={hospitalOverride ? "flex items-end justify-between mb-2 gap-3" : "flex items-end justify-between mb-4"}>
          <div className={hospitalOverride ? "flex-1 min-w-0" : ""}>
            <div className="flex items-center gap-1.5" style={{ fontSize: 10, color: T.textMuted, letterSpacing: "0.5px", textTransform: "uppercase" }}>
              <span>{hospitalOverride ? t('homepage:homeBeta.scenarioCard.hospitalCost') : t('homepage:homeBeta.scenarioCard.estimatedCost')}</span>
              {/* Country picker (default mode only; hospital mode has it on the right column) */}
              {!hospitalOverride && <Popover open={countryPickerOpen} onOpenChange={setCountryPickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-0.5 rounded hover:bg-black/5 px-1 py-0.5 transition-colors"
                    title={t('homepage:homeBeta.scenarioCard.countryPickerTitle', { country: currentCountry?.name || 'United States' })}
                    aria-label={t('homepage:homeBeta.scenarioCard.countryChange')}
                  >
                    {/* SVG flag from flagcdn.com — works on Windows where regional indicator emoji doesn't render */}
                    <img
                      src={`https://flagcdn.com/w20/${(currentCountry?.code || 'us').toLowerCase()}.png`}
                      srcSet={`https://flagcdn.com/w40/${(currentCountry?.code || 'us').toLowerCase()}.png 2x`}
                      alt={currentCountry?.name || 'United States'}
                      width={16}
                      height={12}
                      className="inline-block rounded-sm shadow-sm"
                      loading="lazy"
                    />
                    <ChevronDown className="w-3 h-3" style={{ color: T.textMuted }} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-80 p-0"
                  align="start"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Command>
                    <CommandInput placeholder={t('homepage:homeBeta.scenarioCard.countrySearchPlaceholder')} />
                    <CommandList className="max-h-80">
                      {availableCountries.length <= 1 ? (
                        <CommandEmpty>
                          {intlLoading
                            ? t('homepage:homeBeta.scenarioCard.loadingIntlData')
                            : t('homepage:homeBeta.scenarioCard.noIntlData')}
                        </CommandEmpty>
                      ) : (
                        <CommandEmpty>{t('homepage:homeBeta.scenarioCard.noCountriesFound')}</CommandEmpty>
                      )}
                      {/* Header row explaining the % column */}
                      {availableCountries.length > 1 && (
                        <div
                          className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider flex items-center justify-between border-b"
                          style={{ color: T.textMuted, borderColor: "rgba(27,27,27,0.06)", backgroundColor: "rgba(27,27,27,0.02)" }}
                        >
                          <span>{t('homepage:homeBeta.scenarioCard.countryLabel')}</span>
                          <span>vs US</span>
                        </div>
                      )}
                      <CommandGroup>
                        {availableCountries.map((c) => {
                          const isUS = c.code === "US";
                          const entry = !isUS && intlPricing ? intlPricing[c.code] : null;
                          const intlCost = isUS
                            ? scenario.calculated_avg_cost
                            : entry?.total ?? 0;
                          // Same-subset baseline: intl totals only cover the CPT codes
                          // mapped for that country, so compare against the US cost of
                          // the SAME subset when available. Falling back to the full
                          // scenario cost overstates savings for partial coverage.
                          const usCostBase = entry?.usTotal && entry.usTotal > 0
                            ? entry.usTotal
                            : scenario.calculated_avg_cost;
                          const pctDiff =
                            !isUS && usCostBase > 0 && intlCost > 0
                              ? Math.round(((usCostBase - intlCost) / usCostBase) * 100)
                              : null;
                          const multiplier =
                            !isUS && usCostBase > 0 && intlCost > 0
                              ? usCostBase / intlCost
                              : null;
                          const coverage =
                            entry && entry.totalCodes > 0
                              ? `${entry.mapped}/${entry.totalCodes} procedures`
                              : null;
                          const isSelected = selectedCountry === c.code;
                          const formatCost = (v: number) =>
                            v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v).toLocaleString()}`;

                          return (
                            <CommandItem
                              key={c.code}
                              value={`${c.name} ${c.code}`}
                              onSelect={() => handleCountryChange(c.code)}
                              className="flex items-start gap-2 py-2 cursor-pointer"
                              style={{
                                backgroundColor: isSelected ? "rgba(178,34,52,0.06)" : undefined,
                              }}
                            >
                              <img
                                src={`https://flagcdn.com/w20/${c.code.toLowerCase()}.png`}
                                srcSet={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png 2x`}
                                alt=""
                                width={20}
                                height={15}
                                className="inline-block rounded-sm shadow-sm shrink-0 mt-0.5"
                                loading="lazy"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2">
                                  <span className="text-sm font-medium truncate" style={{ color: T.text }}>
                                    {c.name}
                                  </span>
                                  {isUS ? (
                                    <span className="text-[10px] font-bold shrink-0" style={{ color: T.red }}>
                                      baseline
                                    </span>
                                  ) : pctDiff !== null ? (
                                    <span
                                      className="text-xs font-bold shrink-0 tabular-nums"
                                      style={{
                                        color: pctDiff > 0 ? T.green : T.red,
                                      }}
                                    >
                                      {pctDiff > 0 ? "−" : "+"}{Math.abs(pctDiff)}%
                                    </span>
                                  ) : null}
                                </div>
                                <div className="flex items-baseline justify-between gap-2 mt-0.5">
                                  <span className="text-[11px] font-mono tabular-nums" style={{ color: T.textMuted }}>
                                    {formatCost(intlCost)}
                                    {multiplier !== null && multiplier > 1 && (
                                      <span className="ml-1.5" style={{ color: T.green }}>
                                        ({multiplier.toFixed(1)}× cheaper)
                                      </span>
                                    )}
                                  </span>
                                  {isSelected && (
                                    <span className="text-[10px] uppercase tracking-wider font-bold shrink-0" style={{ color: T.red }}>
                                      ✓ selected
                                    </span>
                                  )}
                                </div>
                                {coverage && (
                                  <div className="text-[10px] mt-0.5" style={{ color: T.textMuted }}>
                                    {coverage} matched
                                  </div>
                                )}
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>}
            </div>
            {(showStatToggle || !hospitalOverride) && (
              <div className="flex items-center gap-0.5 -mt-0.5 mb-0.5 text-[9px] uppercase tracking-wider select-none" style={{ color: T.textMuted as string }}>
                {showStatToggle && (
                  <>
                    <span>{t('homepage:homeBeta.scenarioCard.national')}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleCostStatChange('avg'); }}
                      className="px-1 py-0.5 rounded transition-colors"
                      style={{
                        color: costStat === 'avg' ? T.red : (T.textMuted as string),
                        fontWeight: costStat === 'avg' ? 700 : 400,
                        textDecoration: costStat === 'avg' ? 'underline' : 'none',
                        textUnderlineOffset: 2,
                      }}
                      title={t('homepage:homeBeta.scenarioCard.avgTooltip')}
                    >
                      avg
                    </button>
                    <span aria-hidden style={{ opacity: 0.4 }}>|</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleCostStatChange('median'); }}
                      className="px-1 py-0.5 rounded transition-colors"
                      style={{
                        color: costStat === 'median' ? T.red : (T.textMuted as string),
                        fontWeight: costStat === 'median' ? 700 : 400,
                        textDecoration: costStat === 'median' ? 'underline' : 'none',
                        textUnderlineOffset: 2,
                      }}
                      title={t('homepage:homeBeta.scenarioCard.medianTooltip')}
                    >
                      median
                    </button>
                    <span className="mx-1 opacity-30" aria-hidden>·</span>
                  </>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setHowItWorksOpen(true); }}
                  className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded transition-colors"
                  style={{ color: T.textMuted as string, opacity: 0.85 }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = T.gold as string; e.currentTarget.style.opacity = '1'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = T.textMuted as string; e.currentTarget.style.opacity = '0.85'; }}
                  title={t('homepage:homeBeta.scenarioCard.howItWorks')}
                >
                  <Info className="w-2.5 h-2.5" /> how it works
                </button>
              </div>
            )}
            <p className="flex items-baseline gap-0.5" style={{ fontFamily: serif }}>
              {(() => {
                // Price color: suspicious near-zero > forced context > markup-based > default red
                const priceColor = hospitalOverride
                  ? (suspiciouslyCheap ? '#b45309'
                    : hospitalOverride.priceContext === 'expensive' ? '#ef4444'
                    : hospitalOverride.priceContext === 'deal' ? '#22c55e'
                    : hospitalOverride.blendedMarkup > 0 ? '#ef4444' : '#22c55e')
                  : T.red;
                return (
                  <>
                    {/* Use "≈" (approximation) for partial-coverage scenarios — much harder to misread
                        as a "−" minus sign than the previous tilde. The full label is in the
                        coverage badge below; this glyph is just a quick visual signal that the
                        hospital_total + national-fill blend produced an estimate, not an exact total. */}
                    <span style={{ fontSize: hospitalOverride ? 11 : 13, color: priceColor }}>
                      {hospitalOverride && hospitalOverride.matchedCodes < hospitalOverride.totalCodes ? '≈' : ''}$
                    </span>
                    <span style={{ fontSize: hospitalOverride ? 18 : compact ? 22 : "clamp(22px, 2.8vw, 30px)", color: priceColor, fontWeight: 400 }}>
                      {Math.round(currentCost).toLocaleString()}
                    </span>
                  </>
                );
              })()}
            </p>
            {hospitalOverride && (
              <div className="mt-0.5">
                {(() => {
                  const markup = hospitalOverride.blendedMarkup;
                  const absMarkup = Math.abs(markup);
                  // Near-zero filed price: don't render a "% cheaper" victory badge
                  // for what is almost certainly a filing artifact. Muted amber label,
                  // row stays visible (policy: never hide hospital-filed prices).
                  if (suspiciouslyCheap) {
                    return <span
                      style={{ fontSize: 10, fontWeight: 600, color: '#b45309' }}
                      title={t('common:pricing.hospitalFiledPriceTooltip')}
                    >
                      {t('common:pricing.hospitalFiledPrice')}
                    </span>;
                  }
                  // Use the actual direction — don't blindly trust priceContext, which
                  // just means "this card was placed in the Best Deals rail" and can
                  // be misleading if the hospital is in fact slightly more expensive.
                  if (absMarkup < 1) {
                    return <span style={{ fontSize: 10, fontWeight: 600, color: '#6b7280' }}>{t('common:pricing.approxNationalAvg')}</span>;
                  }
                  if (markup < 0) {
                    return <span style={{ fontSize: 10, fontWeight: 600, color: '#16a34a' }}>
                      {absMarkup >= 99.5 ? t('common:pricing.belowAvgExtreme') : t('common:pricing.percentCheaper', { percent: absMarkup.toFixed(0) })}
                    </span>;
                  }
                  return <span style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: markup > 30 ? '#ef4444' : '#f97316',
                  }}>
                    {t('common:pricing.percentVsNational', { percent: markup.toFixed(0) })}
                  </span>;
                })()}
              </div>
            )}
          </div>
          {/* National avg column (hospital mode only) */}
          {hospitalOverride && (
            <div className="text-center flex-1 min-w-0">
              <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: "0.5px", textTransform: "uppercase" }}>
                {t('homepage:homeBeta.scenarioCard.nationalAvg')}
              </div>
              <p className="flex items-baseline justify-center gap-0.5 mt-0.5" style={{ fontFamily: serif }}>
                <span style={{ fontSize: 11, color: T.textMuted }}>$</span>
                <span style={{ fontSize: 18, color: T.textMuted, fontWeight: 400 }}>
                  {Math.round(hospitalOverride.nationalCost).toLocaleString()}
                </span>
              </p>
              <div style={{ fontSize: 10, color: T.textMuted }}>
                {t('common:pricing.codesCount', { matched: hospitalOverride.matchedCodes, total: hospitalOverride.totalCodes })}
              </div>
            </div>
          )}
          {/* Flag picker (right side, hospital mode) — opens international comparison */}
          {hospitalOverride && (
            <Popover open={countryPickerOpen} onOpenChange={setCountryPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex flex-col items-center gap-1 px-2 py-1.5 rounded-md transition-colors flex-shrink-0 self-end"
                  style={{
                    border: `1px solid ${T.border}`,
                    color: T.textMuted,
                    backgroundColor: "transparent",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.gold; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; }}
                  title={t('homepage:homeBeta.scenarioCard.compareIntl')}
                  aria-label={t('homepage:homeBeta.scenarioCard.compareIntl')}
                >
                  <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                    {t('homepage:homeBeta.scenarioCard.compare')}
                  </div>
                  <div className="flex items-center gap-0.5">
                    <img
                      src={`https://flagcdn.com/w40/${(currentCountry?.code || 'us').toLowerCase()}.png`}
                      alt={currentCountry?.name || 'United States'}
                      width={22}
                      height={16}
                      className="rounded-sm shadow-sm"
                    />
                    <ChevronDown className="w-3 h-3" style={{ color: T.textMuted }} />
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-80 p-0"
                align="end"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Command>
                  <CommandInput placeholder={t('homepage:homeBeta.scenarioCard.countrySearchPlaceholder')} />
                  <CommandList className="max-h-80">
                    {availableCountries.length <= 1 ? (
                      <CommandEmpty>
                        {intlLoading ? t('homepage:homeBeta.scenarioCard.loadingIntlData') : t('homepage:homeBeta.scenarioCard.noIntlData')}
                      </CommandEmpty>
                    ) : (
                      <CommandEmpty>{t('homepage:homeBeta.scenarioCard.noCountriesFound')}</CommandEmpty>
                    )}
                    {availableCountries.length > 1 && (
                      <div
                        className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider flex items-center justify-between border-b"
                        style={{ color: T.textMuted, borderColor: "rgba(27,27,27,0.06)", backgroundColor: "rgba(27,27,27,0.02)" }}
                      >
                        <span>{t('homepage:homeBeta.scenarioCard.countryLabel')}</span>
                        <span>vs US</span>
                      </div>
                    )}
                    <CommandGroup>
                      {availableCountries.map((country) => {
                        const countryEntry = country.code === 'US' ? null : intlPricing?.[country.code];
                        // Same-subset baseline: prefer the entry's own US cost (covers
                        // the identical mapped CPT codes) over the full scenario cost.
                        const usCost = (countryEntry?.usTotal && countryEntry.usTotal > 0)
                          ? countryEntry.usTotal
                          : (hospitalOverride?.nationalCost || 0);
                        const countryCost = country.code === 'US'
                          ? usCost
                          : (countryEntry?.total || 0);
                        const pctDiff = country.code !== 'US' && usCost > 0 && countryCost > 0
                          ? Math.round(((countryCost - usCost) / usCost) * 100)
                          : null;
                        return (
                          <CommandItem
                            key={country.code}
                            value={`${country.name} ${country.code}`}
                            onSelect={() => handleCountryChange(country.code)}
                            className="flex items-center justify-between gap-2 cursor-pointer"
                          >
                            <div className="flex items-center gap-2">
                              <img
                                src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                                alt={country.name}
                                width={16}
                                height={12}
                                className="rounded-sm"
                              />
                              <span style={{ fontSize: 13 }}>{country.name}</span>
                            </div>
                            {pctDiff != null && (
                              <span style={{ fontSize: 11, color: pctDiff < 0 ? T.green : T.red, fontWeight: 600 }}>
                                {pctDiff > 0 ? '+' : ''}{pctDiff}%
                              </span>
                            )}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
          {!hospitalOverride && (
            <div className="flex gap-4 text-right min-w-0">
              <div style={{ minWidth: 64 }}>
                <p style={{ fontSize: 10, color: T.textMuted, letterSpacing: "0.4px", textTransform: "uppercase", whiteSpace: "nowrap" }}>{t('homepage:homeBeta.scenarioCard.duration')}</p>
                {/* Long phase-by-phase durations ("40 weeks pregnancy + 1-2 day
                    birth admission + …") used to wrap into a skinny tall column
                    and shove PROCEDURES off the card edge at 390px — clamp to
                    two lines, full text on the tooltip. */}
                <p
                  title={scenario.duration}
                  style={{
                    fontSize: 12, fontFamily: sans, marginTop: 2, maxWidth: 150, lineHeight: 1.3,
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as never, overflow: "hidden",
                  }}
                >{scenario.duration}</p>
              </div>
              <div className="flex-shrink-0">
                <p style={{ fontSize: 10, color: T.textMuted, letterSpacing: "0.4px", textTransform: "uppercase" }}>{t('homepage:homeBeta.scenarioCard.procedures')}</p>
                <p style={{ fontSize: 13, fontFamily: sans, color: T.blue, marginTop: 2 }}>{scenario.total_procedures}</p>
              </div>
            </div>
          )}
        </div>

        {/* Phase timeline bars — compact version when in hospital mode */}
        {phases.length > 0 && Object.keys(intlBreakdown).length > 0 && (
          <div className={hospitalOverride ? "space-y-0.5 mb-2" : "space-y-1.5 mb-4"}>
            {phases.slice(0, hospitalOverride ? 3 : 4).map((phase, idx) => {
              // In hospital mode, scale national phase costs proportionally to hospital blended price
              let cost = intlBreakdown[phase] || 0;
              if (hospitalOverride && hospitalOverride.nationalCost > 0) {
                const nationalTotal = Object.values(intlBreakdown).reduce((s, v) => s + v, 0);
                if (nationalTotal > 0) {
                  cost = (cost / nationalTotal) * hospitalOverride.blendedPrice;
                }
              }
              const maxCost = hospitalOverride && hospitalOverride.nationalCost > 0
                ? Math.max(...phases.map(p => {
                    const c = intlBreakdown[p] || 0;
                    const nt = Object.values(intlBreakdown).reduce((s, v) => s + v, 0);
                    return nt > 0 ? (c / nt) * hospitalOverride.blendedPrice : c;
                  }), 1)
                : intlMaxPhaseCost;
              const pct = Math.max((cost / maxCost) * 100, 3);
              return (
                <div key={phase} className="flex items-center gap-2">
                  <span
                    className="text-right flex-shrink-0 truncate"
                    style={{ width: hospitalOverride ? 70 : 90, color: T.textMuted, fontSize: hospitalOverride ? 9 : 11 }}
                  >
                    {phase}
                  </span>
                  <div
                    className="flex-1 rounded-sm"
                    style={{ height: hospitalOverride ? 8 : 16, backgroundColor: "rgba(27,27,27,0.04)" }}
                  >
                    <div
                      className="h-full rounded-sm"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: phaseColors[idx % phaseColors.length],
                        minWidth: 3,
                      }}
                    />
                  </div>
                  <span style={{
                    fontSize: hospitalOverride ? 9 : 11,
                    fontFamily: "'JetBrains Mono', monospace",
                    color: T.textMuted,
                    width: hospitalOverride ? 42 : 52,
                    textAlign: "right",
                    flexShrink: 0,
                  }}>
                    ${cost >= 1000 ? `${(cost / 1000).toFixed(0)}k` : Math.round(cost).toLocaleString()}
                  </span>
                </div>
              );
            })}
            {phases.length > (hospitalOverride ? 3 : 4) && (
              <p style={{ fontSize: hospitalOverride ? 9 : 11, color: T.textMuted, textAlign: "right" }}>
                +{phases.length - (hospitalOverride ? 3 : 4)} more
              </p>
            )}
          </div>
        )}

        {/* Attribution line — hide when used inside hospital expansion */}
        {!hospitalOverride && (
          <div className="flex items-center gap-2 mb-3" style={{ fontSize: 12, color: T.textMuted }}>
            <div
              className="flex items-center justify-center rounded-full flex-shrink-0"
              style={{
                width: 22,
                height: 22,
                backgroundColor: "rgba(0,40,104,0.08)",
                fontSize: 10,
                fontWeight: 600,
                color: T.blue,
              }}
            >
              {authorName.charAt(0)}
            </div>
            <span style={{ fontFamily: sans }}>
              @{authorName.replace(/[\s_.]/g, "").toLowerCase()}
            </span>
          </div>
        )}

        {/* CTA buttons */}
        {hospitalOverride ? (
          /* Hospital-row mode: 2 inline buttons (flag picker is up by the price) */
          <div className="mt-auto pt-1 flex gap-1.5">
            <Link
              to={hospitalOverride.hospitalId
                ? `/scenarios/${scenario.id}/${slug}/hospital/${hospitalOverride.hospitalId}`
                : `/scenarios/${scenario.id}/${slug}`
              }
              className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-xs font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: T.red, color: "#fff", minWidth: 0 }}
              title={t('homepage:homeBeta.scenarioCard.breakdownVsHospital')}
            >
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{t('homepage:homeBeta.scenarioCard.viewBreakdown')}</span>
            </Link>
            <Link
              to={`/hospital-rankings?scenario=${scenario.id}`}
              className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-xs font-medium transition-colors"
              style={{
                border: `1px solid ${T.border}`,
                color: T.textMuted,
                backgroundColor: "transparent",
                minWidth: 0,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.blue; e.currentTarget.style.color = T.blue; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; }}
              title={t('homepage:homeBeta.scenarioCard.rankAllForScenario')}
            >
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{t('homepage:homeBeta.scenarioCard.compare')}</span>
            </Link>
          </div>
        ) : sectionNav && onSectionNav ? (
          /* Section-nav mode (scenario page embed): the card's footer is a
             table of contents over the page's own sections — pressing a chip
             opens that section (modal on mobile, scroll on desktop). Replaces
             the self-linking "Explore Full Details" CTA. */
          <div className="mt-auto pt-2">
            <div className="grid grid-cols-3 gap-1.5">
              {sectionNav.map((s) => {
                const ChipIcon = s.icon;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => onSectionNav(s.key)}
                    className="flex items-center justify-center gap-1 py-2 px-1 rounded-lg text-[11px] font-semibold transition-colors"
                    style={{ border: `1px solid ${T.border}`, color: T.textMuted, backgroundColor: "transparent", minWidth: 0 }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.blue; e.currentTarget.style.color = T.blue; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; }}
                  >
                    {ChipIcon && <ChipIcon className="w-3.5 h-3.5 flex-shrink-0" />}
                    <span className="truncate">{s.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* Default mode: stacked CTAs */
          <div className="mt-auto pt-2 space-y-2">
            <Link
              to={`/scenarios/${scenario.id}/${slug}`}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-sm font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: T.red, color: "#fff" }}
            >
              <ExternalLink className="w-4 h-4" /> {t('homepage:homeBeta.scenarioCard.exploreFullDetails')}
            </Link>
            <div className="flex gap-2">
              <Link
                to={`/scenarios/${scenario.id}/${slug}#nearby-hospitals`}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors"
                style={{
                  border: `1px solid ${T.border}`,
                  color: T.textMuted,
                  backgroundColor: "transparent",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.blue; e.currentTarget.style.color = T.blue; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; }}
                title={t('homepage:homeBeta.scenarioCard.hospitalsNear')}
              >
                <MapPin className="w-3.5 h-3.5" /> {t('homepage:homeBeta.scenarioCard.nearMe')}
              </Link>
              <Link
                to={`/scenarios/${scenario.id}/${slug}#care-resources`}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors"
                style={{
                  border: `1px solid ${T.border}`,
                  color: T.textMuted,
                  backgroundColor: "transparent",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.green; e.currentTarget.style.color = T.green; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; }}
                title={t('homepage:homeBeta.scenarioCard.connectToCare')}
              >
                <HandHeart className="w-3.5 h-3.5" /> {t('homepage:homeBeta.scenarioCard.connectToCareLabel')}
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Bottom severity/complication bar with family siblings — hidden in hospital expansion mode.
          Each severity pill is now individually interactive:
            - Hover → HoverCard peek listing the variant names that sit at that severity.
              Communicates that pills are BUCKETS (often holding multiple variants),
              not a single card per level.
            - Click → opens the ScenarioFamilyModal pre-scrolled to that level.
          The "X scenarios" label is its own button that opens the modal at the
          family overview, giving consumers a clean "explore the whole family" entry
          point without forcing them to pick a level first. */}
      {!hospitalOverride && familySiblings && familySiblings.length > 0 && (() => {
        const currentSev = Math.min(Math.max(Math.round(scenario.severity_level ?? 0), 0), 4);
        const sevColors = ['#2b5e49', '#5a9a3c', '#C5A55A', '#e67e22', '#B22234'];
        // Cost-quartile labels — the family's severity ladder is computed from
        // chargemaster price quartiles, not clinical urgency. Saying "CRITICAL"
        // on a $200K row was implying a medical reading the pipeline doesn't
        // perform. Honest cost-tier labels match the dollar ranges in the modal.
        const sevLabels = ['BASE', 'LOW-COST', 'MID-COST', 'HIGH-COST', 'TOP-COST'];
        const sevHumanLabels = ['Base', 'Low-cost', 'Mid-cost', 'High-cost', 'Top-cost'];
        // Group variants by severity (including the current scenario itself) so
        // hover peek can show the actual variant names at each level.
        const variantsByLevel: Record<number, Array<{ id: string; name: string; isCurrent: boolean; estimatedCost?: number | null }>> = { 0: [], 1: [], 2: [], 3: [], 4: [] };
        variantsByLevel[currentSev].push({ id: scenario.id, name: scenario.name, isCurrent: true });
        familySiblings.forEach(s => {
          const lv = Math.min(Math.max(Math.round(s.severity_level ?? 0), 0), 4);
          variantsByLevel[lv].push({ id: s.id, name: s.name, isCurrent: false, estimatedCost: s.estimatedCost });
        });
        const totalFamily = familySiblings.length + 1;
        const slug = scenario.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        const openModalAt = (lv: number | null) => {
          setFamilyModalLevel(lv);
          setFamilyModalOpen(true);
        };
        return (
          <div
            className="flex items-center justify-between px-5 py-3"
            style={{ borderTop: `1px solid ${T.border}`, color: T.text }}
          >
            {/* Left: current severity label → links to current scenario detail (preserves
                prior whole-bar click target). */}
            <Link
              to={`/scenarios/${scenario.id}/${slug}`}
              className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
              style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', color: sevColors[currentSev], textDecoration: 'none' }}
            >
              {sevLabels[currentSev]}
              <span style={{ color: T.textMuted, fontSize: 13 }}>→</span>
            </Link>

            <div className="flex items-center gap-2">
              {/* "N scenarios" → opens family explorer at overview */}
              <button
                type="button"
                onClick={() => openModalAt(null)}
                className="inline-flex items-center gap-1 hover:opacity-80 transition-opacity"
                style={{ fontSize: 11, color: T.textMuted, fontFamily: sans, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
                aria-label={t('homepage:homeBeta.scenarioCardSeverity.exploreFamily')}
                title={t('homepage:homeBeta.scenarioCard.exploreFamily')}
              >
                <Layers className="w-3 h-3" style={{ opacity: 0.7 }} />
                {t('homepage:homeBeta.scenarioCardSeverity.scenarios', { count: totalFamily })}
              </button>
              <div className="flex items-center gap-1">
                {[0, 1, 2, 3, 4].map(lv => {
                  const variants = variantsByLevel[lv];
                  const count = variants.length;
                  const inFamily = count > 0;
                  const isCurrent = lv === currentSev;
                  // Pills with no variants in this family stay disabled — preserves the
                  // "this severity isn't represented for this diagnosis" affordance.
                  if (!inFamily) {
                    return (
                      <span
                        key={lv}
                        style={{
                          width: 18, height: 18, borderRadius: '50%',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 700,
                          backgroundColor: 'transparent',
                          color: T.textMuted,
                          border: `1px solid ${T.border}`,
                          opacity: 0.4,
                        }}
                        aria-hidden
                      >
                        {lv}
                      </span>
                    );
                  }
                  // Active pill — hover-peek + click-to-open-modal.
                  return (
                    <HoverCard key={lv} openDelay={120} closeDelay={60}>
                      <HoverCardTrigger asChild>
                        <button
                          type="button"
                          onClick={() => openModalAt(lv)}
                          aria-label={`${t(`homepage:homeBeta.severityLadder.${['base','lowCost','midCost','highCost','topCost'][lv]}`, sevHumanLabels[lv])} · ${count} ${count === 1 ? t('homepage:homeBeta.familyModal.variantSingular') : t('homepage:homeBeta.familyModal.variantPlural')}`}
                          style={{
                            width: 18, height: 18, borderRadius: '50%',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 10, fontWeight: 700,
                            backgroundColor: isCurrent ? sevColors[lv] : 'transparent',
                            color: isCurrent ? '#fff' : sevColors[lv],
                            border: `1px solid ${sevColors[lv]}`,
                            padding: 0, cursor: 'pointer',
                            // Tiny stacked-shadow tell when the bucket holds >1 variant —
                            // mirrors the stacked-card visual on /scenarios/:uuid/ so the
                            // metaphor reads consistently across surfaces.
                            boxShadow: count > 1
                              ? `1px 1px 0 ${sevColors[lv]}55, 2px 2px 0 ${sevColors[lv]}25`
                              : 'none',
                          }}
                        >
                          {lv}
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent
                        side="top"
                        align="end"
                        sideOffset={6}
                        className="w-64 p-0"
                        style={{ backgroundColor: T.cardBg, border: `1px solid ${T.border}`, color: T.text }}
                      >
                        <div className="px-3 py-2" style={{ borderBottom: `1px solid ${T.border}` }}>
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-flex items-center justify-center text-[10px] font-bold"
                              style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: sevColors[lv], color: '#fff' }}
                            >
                              {lv}
                            </span>
                            <span className="text-[12px] font-semibold" style={{ color: sevColors[lv] }}>
                              {t(`homepage:homeBeta.severityLadder.${['base','lowCost','midCost','highCost','topCost'][lv]}`, sevHumanLabels[lv])}
                            </span>
                            <span className="text-[10px] ml-auto" style={{ color: T.textMuted }}>
                              {count} {count === 1 ? t('homepage:homeBeta.familyModal.variantSingular') : t('homepage:homeBeta.familyModal.variantPlural')}
                            </span>
                          </div>
                          <p className="mt-1 text-[10px]" style={{ color: T.textMuted, lineHeight: 1.4 }}>
                            {lv === 0
                              ? t('homepage:homeBeta.scenarioCardSeverity.baseline')
                              : t('homepage:homeBeta.scenarioCardSeverity.quartile', { level: lv })}
                          </p>
                        </div>
                        <ul className="px-3 py-2 space-y-1 max-h-44 overflow-y-auto">
                          {variants.slice(0, 6).map(v => (
                            <li key={v.id} className="text-[11px] flex items-start gap-2" style={{ color: T.text, lineHeight: 1.35 }}>
                              <span style={{ color: sevColors[lv], fontWeight: 700 }}>·</span>
                              <span className="flex-1">
                                {v.name}
                                {v.isCurrent && (
                                  <span className="ml-1 text-[9px] font-semibold uppercase" style={{ color: T.textMuted }}>{t('homepage:homeBeta.scenarioCardSeverity.thisCard')}</span>
                                )}
                              </span>
                            </li>
                          ))}
                          {variants.length > 6 && (
                            <li className="text-[10px] italic" style={{ color: T.textMuted }}>
                              {t('homepage:homeBeta.scenarioCardSeverity.moreVariants', { count: variants.length - 6 })}
                            </li>
                          )}
                        </ul>
                        <div className="px-3 py-2 text-[10px] text-center" style={{ borderTop: `1px solid ${T.border}`, color: T.textMuted }}>
                          {t('homepage:homeBeta.scenarioCard.openFamilyExplorerFooter')}
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* "How it works" modal — Dignity-narrated breakdown of code list,
          cornerstones, and the per-code methodology. Lazy-loaded query
          inside the modal so it pays no cost on card mount. */}
      <ScenarioHowItWorksModal
        scenarioId={scenario.id}
        scenarioName={scenario.name}
        scenarioDescription={scenario.description}
        open={howItWorksOpen}
        onOpenChange={setHowItWorksOpen}
      />

      {/* Family explorer modal — surfaces the full severity ladder for this
          scenario's family. Wired up by the bottom-bar pills + "N scenarios"
          button. Mounted only when familySiblings is present so cards without
          family data pay nothing. */}
      {familySiblings && familySiblings.length > 0 && (
        <ScenarioFamilyModal
          scenario={{
            id: scenario.id,
            name: scenario.name,
            description: scenario.description,
            icon_name: scenario.icon_name,
            color: scenario.color,
            scenario_family_id: familySyntheticId,
          }}
          allRawScenarios={familyAllScenarios}
          open={familyModalOpen}
          onOpenChange={setFamilyModalOpen}
          theme={T}
          defaultLevel={familyModalLevel}
        />
      )}
    </div>
  );
});
